import {
  Mailbox,
  Plus,
  Clock,
  MapPin,
  Package,
  BarChart3,
  CreditCard,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { OnboardingEmptyState } from '@/client/components/ui/empty-state';

export function DirectMailPage() {
  return (
    <div className="space-y-6 stagger-children relative">
      {/* Decorative background gradient */}
      <div className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-sky-500/20 via-blue-500/10 to-transparent blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-blue-500/20 via-sky-500/10 to-transparent blur-3xl" />
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 relative">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <div className="size-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg shadow-sky-500/20">
              <Mailbox className="size-5 text-white" />
            </div>
            Direct Mail
          </h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            Send physical postcards, letters, and catalogs
          </p>
        </div>
        <Button
          onClick={() => toast.info('Direct Mail coming soon!')}
          size="lg"
          className="rounded-xl glow gap-2 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
        >
          <Plus className="size-5" />
          Create Mailing
        </Button>
      </div>

      {/* Empty State */}
      <OnboardingEmptyState
        icon={Mailbox}
        title="Send your first mailing"
        steps={[
          { icon: FileText, text: 'Design postcards or letters with your branding' },
          { icon: MapPin, text: 'Upload addresses or use your form contacts' },
          { icon: Package, text: 'We print, stuff, and mail — you track delivery' },
        ]}
        action={{
          label: 'Create your first mailing',
          onClick: () => toast.info('Direct Mail coming soon!'),
          icon: Plus,
        }}
      />

      {/* Coming Soon Banner */}
      <Card className="rounded-2xl border-2 border-dashed border-sky-300 dark:border-sky-800 bg-gradient-to-r from-sky-50/50 to-blue-50/50 dark:from-sky-950/20 dark:to-blue-950/20">
        <CardContent className="flex flex-col sm:flex-row items-center gap-4 p-6">
          <div className="size-16 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg">
            <Clock className="size-8 text-white" />
          </div>
          <div className="text-center sm:text-left flex-1">
            <h3 className="text-lg font-semibold mb-1">Direct Mail Coming Soon</h3>
            <p className="text-sm text-muted-foreground">
              We&apos;re building an easy way to send physical mail — postcards, letters, and
              self-mailers. Perfect for event invitations, thank you notes, fundraising appeals, and
              seasonal campaigns. Upload your design or use our templates.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Feature Preview */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-950/30 dark:to-blue-950/30">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg shadow-sky-500/20">
              <FileText className="size-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-medium">Postcards & Letters</p>
              <p className="text-lg font-semibold">Multiple Formats</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <MapPin className="size-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-medium">Address Verification</p>
              <p className="text-lg font-semibold">USPS Validated</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <BarChart3 className="size-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-medium">Delivery Tracking</p>
              <p className="text-lg font-semibold">Real-time Status</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Use Cases */}
      <Card className="rounded-2xl border-0 bg-gradient-to-br from-muted/30 to-muted/10">
        <CardContent className="p-6">
          <h4 className="font-semibold mb-4">Perfect for Armenian organizations</h4>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="size-2 rounded-full bg-sky-500" />
              Event invitations and save-the-dates
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="size-2 rounded-full bg-sky-500" />
              Holiday cards and seasonal greetings
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="size-2 rounded-full bg-sky-500" />
              Fundraising appeals and thank you notes
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="size-2 rounded-full bg-sky-500" />
              Advocacy postcards to elected officials
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Note */}
      <Card className="rounded-2xl border-0 bg-gradient-to-br from-primary/5 to-accent/5">
        <CardContent className="flex items-start gap-4 p-6">
          <div className="size-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shrink-0">
            <CreditCard className="size-5 text-white" />
          </div>
          <div>
            <h4 className="font-semibold mb-1">Simple, transparent pricing</h4>
            <p className="text-sm text-muted-foreground">
              Pay only for what you send. Postcards start at $0.70 each with bulk discounts for
              larger mailings. No monthly fees, no minimums — just straightforward pricing that
              works for organizations of all sizes.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
